import {Injectable} from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import {AppData} from "../AppData";
import {ChecklistTemplateEntry} from "../../models/checklist-template-entry/checklist-template-entry.model";
import {UrlBuilder} from "../../models/frontend_entities/UrlBuilder";
import Constants from "../../Constants";
import {firstValueFrom, Observable} from "rxjs";
import {ChecklistEntryType} from "../../enums/checklist-entry/checklist-entry-type.enum";
import {
  ChecklistTemplateEntryNumbering
} from "../../models/checklist-template/checklist-template-entry-numbering.model";
import {
  ChecklistTemplateEntryOption
} from "../../models/checklist-template-entry/checklist-template-entry-option.model";
import {
  CreateChecklistTemplateEntryDto
} from "../../models/checklist-template-entry/create-checklist-template-entry.model";
import {
  CreateChecklistTemplateEntriesFromDataFieldsDto
} from "../../models/checklist-template-entry/create-checklist-template-entries-from-data-fields-dto.model";
import {
  ChecklistTemplateEntryCondition
} from "../../models/checklist-template-entry/checklist-template-entry-condition.model";
import {ArrayUtils} from "../../utils/ArrayUtils";
import {
  ChecklistTemplateEntryPossibleConditions
} from "../../models/checklist-template-entry/checklist-template-entry-possible-conditions.model";

@Injectable()
export class ChecklistTemplateEntryRequestService {

  //region CONSTRUCTOR
  constructor(
    private http: HttpClient,
    private data: AppData
  ) {}
  //endregion

  //region GET
  public getChecklistTemplateEntry(
    checklistTemplateId: string,
    checklistTemplateEntryId: string
  ): Observable<ChecklistTemplateEntry> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.entryById
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.get<ChecklistTemplateEntry>(url);
  }

  public getChecklistTemplateEntries(
    checklistTemplateId: string
  ): Promise<ChecklistTemplateEntry[]> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.entries
        .replace("%1", checklistTemplateId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return firstValueFrom(this.http.get<ChecklistTemplateEntry[]>(url));
  }

  public getChecklistTemplateEntriesAvailableForConversion(
    checklistTemplateId: string,
    tableEntryId: string
  ): Observable<ChecklistTemplateEntry[]> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.entriesAvailableForConversion
        .replace("%1", checklistTemplateId)
        .replace("%2", tableEntryId.toString())
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.get<ChecklistTemplateEntry[]>(url);
  }


  public getTableRowsForTable(
    checklistTemplateId: string,
    tableEntryId: string,
  ): Observable<ChecklistTemplateEntry[]> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.entriesOfTableEntry
        .replace("%1", checklistTemplateId)
        .replace("%2", tableEntryId.toString())
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.get<ChecklistTemplateEntry[]>(url);
  }

  public getNumberingForEntry(
    checklistTemplateId: string,
    type: ChecklistEntryType,
    insertAfterEntryId: string,
    insertBeforeEntryId: string,
    column: number
  ): Promise<ChecklistTemplateEntryNumbering> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.numbering
        .replace("%1", checklistTemplateId)
      )
        .param('type', type)
        .param('insertAfterEntryId', insertAfterEntryId)
        .param('insertBeforeEntryId', insertBeforeEntryId)
        .param('column', column?.toString())
        .param('accessMode', this.data.getAccessMode())
        .build();

    return firstValueFrom(this.http.get<ChecklistTemplateEntryNumbering>(url));
  }

  public getAssessmentOptions(): Promise<ChecklistTemplateEntryOption[]> {
    const url: string = new UrlBuilder(Constants.URLS.checklistTemplates.assessmentOptions)
      .param('accessMode', this.data.getAccessMode())
      .build();

    return firstValueFrom(this.http.get<ChecklistTemplateEntryOption[]>(url));
  }

  public getChecklistTemplateEntryCondition(
    checklistTemplateId: string,
    checklistTemplateEntryId: string,
    checklistTemplateEntryConditionId: string,
  ): Observable<ChecklistTemplateEntryCondition> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.conditionWithId
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
        .replace("%3", checklistTemplateEntryConditionId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.get<ChecklistTemplateEntryCondition>(url);
  }

  public getPossibleConditionsForEntry(
    checklistTemplateId: string,
    checklistTemplateEntryId: string
  ): Observable<ChecklistTemplateEntryPossibleConditions[]> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.possibleConditionsForEntry
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.get<ChecklistTemplateEntryPossibleConditions[]>(url);
  }

  public getPossibleConditionsForEntryAndCondition(
    checklistTemplateId: string,
    checklistTemplateEntryId: string,
    checklistTemplateEntryConditionId: string
  ): Observable<ChecklistTemplateEntryPossibleConditions[]> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.possibleConditionsForEntryAndCondition
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
        .replace("%3", checklistTemplateEntryConditionId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.get<ChecklistTemplateEntryPossibleConditions[]>(url);
  }
  //endregion

  //region POST
  public createChecklistTemplateEntry(
    checklistTemplateId: string,
    createChecklistTemplateEntryDto: CreateChecklistTemplateEntryDto,
    entryImage: File,
    optionImages: Map<number,File>
  ): Promise<ChecklistTemplateEntry> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.entry
        .replace("%1", checklistTemplateId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    const requestForm = new FormData();

    requestForm.append('entryImage', entryImage? entryImage : new File([],null));
    requestForm.append('dto', new Blob([JSON.stringify(createChecklistTemplateEntryDto)], {type: "application/json"}));


    if (optionImages?.size) {
      optionImages.forEach( (value, key) => {
        requestForm.append('optionImages', value);
      });
      requestForm.append('optionImagesIndices',new Blob([JSON.stringify([...optionImages.keys()])], {type: 'application/json'}));
    }

    const headers = new HttpHeaders({
      'cp-noContentType': "true"
    });

    return firstValueFrom(this.http.post<ChecklistTemplateEntry>(url, requestForm, {headers: headers}));
  }

  public createChecklistTemplateEntriesFromDataFields(
    checklistTemplateId: string,
    createChecklistTemplateEntriesFromDataFieldsDto: CreateChecklistTemplateEntriesFromDataFieldsDto
  ): Promise<ChecklistTemplateEntry[]> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.createEntriesFromDataFields
        .replace("%1", checklistTemplateId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return firstValueFrom(this.http.post<ChecklistTemplateEntry[]>(
      url,
      createChecklistTemplateEntriesFromDataFieldsDto
    ));
  }

  public createChecklistTemplateEntryCondition(
    checklistTemplateId: string,
    checklistTemplateEntryId: string,
    checklistTemplateEntryCondition: ChecklistTemplateEntryCondition
  ): Observable<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.condition
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.post<void>(url, checklistTemplateEntryCondition);
  }


  public convertEntryToTableRow(
    checklistTemplateId: string,
    tableEntryId: string,
    entriesToBeConverted: string[]
  ): Observable<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.entryToTableRow
        .replace("%1", checklistTemplateId)
        .replace("%2", tableEntryId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.post<void>(url, entriesToBeConverted);
  }

  public convertTableRowToEntry(
    checklistTemplateId: string,
    tableEntryId: string,
    entriesToBeConverted: string[]
  ): Observable<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.tableRowToEntry
        .replace("%1", checklistTemplateId)
        .replace("%2", tableEntryId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.post<void>(url, entriesToBeConverted);
  }

  public pasteDuplicateEntries(
    targetChecklistTemplateVersionId: string,
    sourceChecklistTemplateEntryId: string,
    duplicateWholeBlock: boolean,
    pasteBeforeEntryId: string,
    pasteAfterEntryId: string,
  ): Observable<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.pasteDuplicateEntries
        .replace("%1", targetChecklistTemplateVersionId)
      )
        .param('sourceChecklistTemplateEntryId', sourceChecklistTemplateEntryId)
        .param('duplicateBlock', duplicateWholeBlock)
        .param('beforeEntryId', pasteBeforeEntryId)
        .param('afterEntryId', pasteAfterEntryId)
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.post<void>(url,null);
  }

  public pasteDuplicateAllEntries(
    targetChecklistTemplateVersionId: string,
    sourceChecklistTemplateVersionEntry: string,
    pasteBeforeEntryId: string,
    pasteAfterEntryId: string,
  ): Observable<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.pasteDuplicateAllEntries
        .replace("%1", targetChecklistTemplateVersionId)
      ).param('accessMode', this.data.getAccessMode())
        .param('sourceChecklistTemplateVersionId', sourceChecklistTemplateVersionEntry)
        .param('beforeEntryId', pasteBeforeEntryId)
        .param('afterEntryId', pasteAfterEntryId)
        .build();

    return this.http.post<void>(url,null);
  }
  //endregion

  //region PUT
  public updateChecklistTemplateEntry(
    checklistTemplateId: string,
    checklistTemplateEntry: ChecklistTemplateEntry,
    entryImage: File,
    optionImages: Map<number,File>
  ): Promise<ChecklistTemplateEntry> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.entry
        .replace("%1", checklistTemplateId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    const headers = new HttpHeaders({
      'cp-noContentType': "true"
    });

    const requestForm = new FormData();

    if( entryImage ) {
      requestForm.append('entryImage', entryImage);
    }
    requestForm.append('dto', new Blob([JSON.stringify(checklistTemplateEntry)], {type: 'application/json'}));

    if (optionImages?.size) {
      optionImages.forEach( (value, key) => {
        requestForm.append('optionImages', value);
      });
      requestForm.append('optionImagesIndices',new Blob([JSON.stringify([...optionImages.keys()])], {type: 'application/json'}));
    }

    return firstValueFrom(this.http.put<ChecklistTemplateEntry>(url, requestForm, {headers: headers}));
  }

  public groupWithPrevious(
    checklistTemplateId: string,
    checklistTemplateEntryId: string
  ): Promise<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.groupWithPrevious
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return firstValueFrom(this.http.put<any>(url, null));
  }

  public ungroupWithPrevious(
    checklistTemplateId: string,
    checklistTemplateEntryId: string
  ): Promise<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.ungroupWithPrevious
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return firstValueFrom(this.http.put<any>(url, null));
  }

  public reorderChecklistEntries(
    checklistTemplateId: string,
    fromBlockFirstId: string,
    toBlockFirstId: string
  ): Promise<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.reorderChecklistEntries
        .replace("%1", checklistTemplateId)
      )
        .param('accessMode', this.data.getAccessMode())
        .param('fromBlockFirstId', fromBlockFirstId)
        .param('toBlockFirstId',toBlockFirstId)
        .build();
    return firstValueFrom(this.http.put<any>(url,null));
  }


  public moveEntryBack(
    checklistTemplateId: string,
    checklistTemplateEntryId: string
  ): Promise<void> {
    const url = new UrlBuilder(Constants.URLS.checklistTemplates.moveEntryBack
      .replace("%1", checklistTemplateId)
      .replace("%2", checklistTemplateEntryId)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return firstValueFrom(this.http.put<void>(url,null));
  }

  public moveEntryForward(
    checklistTemplateId: string,
    checklistTemplateEntryId: string
  ): Promise<void> {
    const url = new UrlBuilder(Constants.URLS.checklistTemplates.moveEntryForward
      .replace("%1", checklistTemplateId)
      .replace("%2", checklistTemplateEntryId)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return firstValueFrom(this.http.put<void>(url,null));
  }

  public updateChecklistTemplateEntryCondition(
    checklistTemplateId: string,
    checklistTemplateEntryId: string,
    checklistTemplateEntryConditionId: string,
    checklistTemplateEntryCondition: ChecklistTemplateEntryCondition
  ): Observable<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.conditionWithId
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
        .replace("%3", checklistTemplateEntryConditionId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.put<void>(url, checklistTemplateEntryCondition);
  }

  public shiftChecklistTemplateEntryLevelToLeft(
    checklistTemplateId: string,
    checklistTemplateEntryId: string,
    shiftLeft: boolean
  ) {
    const url: string = new UrlBuilder(Constants.URLS.checklistTemplates.shiftLevelLeft
      .replace("%1", checklistTemplateId)
      .replace("%2", checklistTemplateEntryId)
    )
      .param('shiftLeft',shiftLeft)
      .param('accessMode', this.data.getAccessMode())
      .build();

    return this.http.put<void>(url,null);
  }
  //endregion

  //region DELETE
  public deleteChecklistTemplateEntry(
    checklistTemplateId: string,
    checklistTemplateEntryId: string,
    wholeBlock: boolean
  ): Promise<void> {
    const url: string =
      new UrlBuilder(
          Constants.URLS.checklistTemplates.deleteEntryById
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
      )
        .param('wholeBlock', wholeBlock)
        .param('accessMode', this.data.getAccessMode())
        .build();

    return firstValueFrom(this.http.delete<void>(url));
  }

  public deleteEntryImage(
    checklistTemplateId: string,
    checklistTemplateEntryId: string
  ) {
    const url: string = new UrlBuilder(Constants.URLS.checklistTemplates.deleteEntryImage
      .replace("%1",checklistTemplateId)
      .replace("%2",checklistTemplateEntryId)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return firstValueFrom(this.http.delete<void>(url));
  }

  public deleteOptionImage(
    checklistTemplateId: string,
    checklistTemplateEntryId: string,
    checklistTemplateOptionId: string
  ) {
    const url: string = new UrlBuilder(Constants.URLS.checklistTemplates.deleteOptionImage
      .replace("%1",checklistTemplateId)
      .replace("%2",checklistTemplateEntryId)
      .replace("%3",checklistTemplateOptionId)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return firstValueFrom(this.http.delete<void>(url));
  }

  public deleteChecklistTemplateEntryCondition(
    checklistTemplateId: string,
    checklistTemplateEntryId: string,
    checklistTemplateEntryConditionId: string
  ): Observable<void> {
    const url: string =
      new UrlBuilder(Constants.URLS.checklistTemplates.conditionWithId
        .replace("%1", checklistTemplateId)
        .replace("%2", checklistTemplateEntryId)
        .replace("%3", checklistTemplateEntryConditionId)
      )
        .param('accessMode', this.data.getAccessMode())
        .build();

    return this.http.delete<void>(url);
  }
  //endregion
}
